import { useTranslation } from 'react-i18next';

import { useGetMe } from '../../api/generated/identity/identity';
import { useGetMyBilling } from '../../api/generated/ledger/ledger';
import type { BillingSummary, Charge, Me } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { formatDateInPersonTimezone } from '../../i18n/datetime';
import { formatMoney, openTotals } from '../billing/money';
import { useAllMyCharges } from '../billing/myCharges';
import { PanelSection, panelState } from './PanelSection';
import {
  NextClass,
  nextUpcoming,
  occurrencesPanelQuery,
  useHomeOccurrences,
} from './UpcomingClasses';
import styles from './dashboard.module.css';

/**
 * Home do aluno.
 *
 * Ela mostra as duas coisas que o aluno abre a plataforma para saber: quando e a
 * proxima aula e quanto ele deve. As tres leituras que sustentam isso ja existem
 * como operacao normativa — `listOccurrences`, `getMyBilling` e `listMyCharges`
 * — e nenhuma delas precisou de contrato novo. O extrato entra ao lado do resumo
 * porque o `charges` do resumo e uma pagina, e dinheiro nao se soma sobre uma
 * pagina: ver `useAllMyCharges`.
 *
 * O cartao "Em breve" que ficava aqui foi retirado: a primeira tela depois do
 * login nao e lugar de pedir desculpa por entrega futura.
 */
export function StudentHome() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;

  // A janela inteira, e nao a primeira pagina dela: ver `useHomeOccurrences`.
  // "Nada precisa da sua atencao agora" e uma afirmacao sobre o servidor, e com
  // a primeira pagina inteira desmarcada a aula que sobra fica atras do cursor.
  const occurrences = useHomeOccurrences(!!me);
  const billing = useGetMyBilling({ query: { enabled: !!me } });
  /**
   * O extrato inteiro, e nao o `charges` do resumo.
   *
   * O resumo carrega uma PAGINA de 50 faturas, e era sobre ela que a home
   * somava "Em aberto" — a primeira tela depois do login SUBESTIMAVA a divida
   * de quem tem historico longo, em silencio, enquanto o proprio resumo trazia
   * ao lado o total que o banco apura sobre a tabela inteira. Ver
   * `useAllMyCharges`: a mesma leitura, com a mesma justificativa, que o portal
   * financeiro usa, para que as duas telas nao voltem a discordar sobre a mesma
   * divida.
   */
  const charges = useAllMyCharges({ enabled: !!me });

  if (!me) return null;

  const next = panelState(occurrencesPanelQuery(occurrences), (items) => nextUpcoming(items));
  // Financeiro vazio e nao ter fatura nenhuma. Saldo zerado com fatura paga e
  // informacao — dizer "nenhuma fatura por aqui" a quem acabou de pagar apagaria
  // a prova do pagamento da tela onde ele foi procurar.
  //
  // O cartao depende das DUAS leituras, e por isso o estado delas entra junto:
  // enquanto o cursor do extrato nao acabou nao ha total a mostrar (mostrar o
  // parcial seria mostrar uma divida menor que a real), e a leitura que falhou
  // nao pode virar "nenhuma fatura por aqui" — que e a frase que faz o aluno
  // com fatura vencida fechar a tela tranquilo.
  const finance = panelState(
    {
      isError: billing.isError || charges.failed,
      data:
        billing.data !== undefined && charges.complete
          ? { summary: billing.data, charges: charges.charges }
          : undefined,
    },
    (loaded) =>
      loaded.charges.length === 0 && loaded.summary.outstanding.amountCents === 0
        ? undefined
        : loaded,
  );

  return (
    <>
      <h1 className={styles.title}>{t('dashboard.student.title')}</h1>

      <PanelSection
        id="student-next-action"
        title={t('dashboard.nextAction')}
        state={next}
        empty={t('dashboard.nextActionEmpty')}
      >
        {(occ) => <NextClass me={me} occ={occ} />}
      </PanelSection>

      <PanelSection
        id="student-billing"
        title={t('billing.title')}
        state={finance}
        empty={t('billing.chargesEmpty')}
      >
        {({ summary, charges: todas }) => (
          <BillingFigures me={me} summary={summary} charges={todas} />
        )}
      </PanelSection>
    </>
  );
}

/**
 * O resumo financeiro, e nao o portal.
 *
 * Aqui nao ha botao de pagar: pagar passa pelo checkout hospedado, que tem tela
 * propria e regras proprias. A home diz o que esta em aberto e quando vence; o
 * resto e um clique no menu.
 *
 * **"Em aberto" e o mesmo numero que a tela de faturas mostra**, e sai do mesmo
 * `openTotals` sobre a mesma colecao. `summary.outstanding` soma `pending`,
 * `processing` e `overdue` na mesma conta (`SumOutstandingByPerson`), e
 * escreve-lo sob o rotulo "Em aberto" cobrava do aluno um dinheiro que ele ja
 * mandou: com R$ 120,00 a pagar e R$ 269,00 esperando o banco, a primeira tela
 * depois do login dizia "Em aberto R$ 389,00". A Onda 1 desfez isso em
 * `/billing` e deixou a home intacta — duas telas, dois numeros, sobre a mesma
 * divida.
 *
 * A conta e feita sobre `charges`, que chega de `useAllMyCharges` **completo**,
 * e nao sobre `summary.charges`, que e uma pagina de 50. A separacao dos dois
 * totais so pode sair da lista de faturas — e um total precisa poder ser
 * conferido somando o que esta escrito na tela —, mas a lista precisa ser a
 * lista: somada sobre uma pagina, a divida sai menor do que e, e mostrar divida
 * a menos e o unico erro desta tela que o aluno nao tem como perceber.
 */
function BillingFigures({
  me,
  summary,
  charges,
}: {
  readonly me: Me;
  readonly summary: BillingSummary;
  readonly charges: readonly Charge[];
}) {
  const { t } = useTranslation();
  // A moeda continua vindo de `outstanding`: e o campo do contrato que a
  // declara. O que a home deixou de usar dele e o **valor**.
  const totals = openTotals(charges, summary.outstanding.currency);

  return (
    <>
      {/*
        Acesso bloqueado por debito e a unica coisa desta tela que nao pode
        esperar o aluno abrir outra: ela explica por que o resto parou.
      */}
      {summary.blockedAccess ? (
        <Alert tone="error" title={t('billing.accessBlocked.title')}>
          {t('billing.accessBlocked.detail')}
        </Alert>
      ) : null}

      <ul className={styles.list}>
        <li className={styles.listItem}>
          <span>{t('billing.outstanding')}</span>
          <span>{formatMoney(totals.awaitingPayment, me.locale)}</span>
        </li>
        {/*
          O que ja esta em processamento aparece separado, e so quando existe —
          a mesma regra da tela de faturas. Somado ao de cima ele vira cobranca
          de novo; zerado, e ruido: nao ha nada a esperar do banco.
        */}
        {totals.inProcessing.amountCents > 0 ? (
          <li className={styles.listItem}>
            <span>{t('billing.chargeState.processing')}</span>
            <span>{formatMoney(totals.inProcessing, me.locale)}</span>
          </li>
        ) : null}
        <li className={styles.listItem}>
          <span>{t('billing.nextDue')}</span>
          <span>
            {summary.nextDueOn
              ? formatDateInPersonTimezone(summary.nextDueOn, me.timezone, me.locale)
              : t('billing.nextDueNone')}
          </span>
        </li>
      </ul>
    </>
  );
}
